import type { MouseEvent } from 'react';
import type {
  NetworkGraphEdgeModel,
  NetworkGraphNodeModel,
} from './NetworkGraph';
import styles from './NetworkGraph.module.scss';
import type { NetworkGraphPosition } from './utils/computeRadialLayout';
import { edgeGeometry } from './utils/networkGraphEdgeUtils';

// Matches the old 8×8 marker chevron (M 1 1 L 7 4 L 1 7).
const ARROW_LENGTH = 3;
const ARROW_HALF_WIDTH = 3;
const SELECTED_ARROW_LENGTH = 5;
const SELECTED_ARROW_HALF_WIDTH = 5;

export interface NetworkGraphEdgeProps {
  edge: NetworkGraphEdgeModel;
  source: NetworkGraphNodeModel;
  sourcePosition: NetworkGraphPosition;
  target: NetworkGraphNodeModel;
  targetPosition: NetworkGraphPosition;
  targetAngleOffset?: number;
  // Non-zero when a reverse edge exists; shifts the shaft into its own lane.
  lateralOffset?: number;
  // Hover driven from outside the shaft's own pointer area (a hovered node at
  // either end), painted like the CSS :hover state.
  isHovered?: boolean;
  onClick?: () => void;
  onHover?: (isHovered: boolean) => void;
}

// Drawn in the edge group (not an SVG <marker>) so stroke inherits the same
// currentColor as the shaft — context-stroke markers resolve to page color.
const arrowheadPath = (
  tip: NetworkGraphPosition,
  from: NetworkGraphPosition,
  size: { length: number; halfWidth: number } = {
    length: ARROW_LENGTH,
    halfWidth: ARROW_HALF_WIDTH,
  }
) => {
  const angle = Math.atan2(tip.y - from.y, tip.x - from.x);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const baseX = tip.x - cos * size.length;
  const baseY = tip.y - sin * size.length;
  const perpX = -sin * size.halfWidth;
  const perpY = cos * size.halfWidth;

  return `M ${baseX + perpX} ${baseY + perpY} L ${tip.x} ${tip.y} L ${baseX - perpX} ${baseY - perpY}`;
};

export const NetworkGraphEdge = ({
  edge,
  source,
  sourcePosition,
  target,
  targetPosition,
  targetAngleOffset,
  lateralOffset = 0,
  isHovered,
  onClick,
  onHover,
}: NetworkGraphEdgeProps) => {
  const isSelected = edge.emphasis === 'selected';
  const { start, end } = edgeGeometry({
    source,
    sourcePosition,
    target,
    targetPosition,
    targetAngleOffset,
    lateralOffset,
  });
  const path = `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
  // Two-way tips only for reciprocal pairs (edge.twoWayArrow): a merged A↔B edge
  // shows a head at each end, while a one-way edge keeps its single
  // backend-direction arrow even on hover/select.
  const showTwoWayArrow = edge.twoWayArrow === true;
  const arrowSize = isSelected
    ? { length: SELECTED_ARROW_LENGTH, halfWidth: SELECTED_ARROW_HALF_WIDTH }
    : { length: ARROW_LENGTH, halfWidth: ARROW_HALF_WIDTH };

  const handleClick = (event: MouseEvent<SVGPathElement>) => {
    if (!onClick) {
      return;
    }
    event.stopPropagation();
    onClick();
  };

  const handleHover = (hovered: boolean) => {
    onHover?.(hovered);
  };

  return (
    <g
      className={styles.edgeGroup}
      data-emphasis={edge.emphasis}
      data-hovered={isHovered || undefined}
      data-network-graph-edge-id={edge.id}
      data-testid="network-graph-edge"
    >
      <path className={styles.edge} d={path} />
      <path
        aria-hidden="true"
        className={styles.edge}
        d={arrowheadPath(end, start, arrowSize)}
        data-network-graph-arrow="end"
      />
      {showTwoWayArrow ? (
        <path
          aria-hidden="true"
          className={styles.edge}
          d={arrowheadPath(start, end, arrowSize)}
          data-network-graph-arrow="start"
        />
      ) : null}
      <path
        aria-hidden="true"
        className={styles.edgeHitArea}
        data-clickable={onClick ? true : undefined}
        data-network-graph-edge-hit-area
        d={path}
        onClick={handleClick}
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
        // role="button" opts the edge out of the SVG pan-capture in
        // useFlowChartPanZoom so its click is not swallowed by drag-to-pan.
        role={onClick ? 'button' : undefined}
      />
    </g>
  );
};
